import ColumnFilter from "components/ColumnFilter";
import useDetectFilterChange from "hooks/useDetectFilterChange";
import useUrlFilters from "hooks/useUrlFilters";
import {
  includes,
  isEmpty,
  isFunction,
  map,
  noop,
  omit,
  omitBy,
  pick,
  uniqBy,
} from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CancellableAPIRequest, getEmptyCancellableRequest } from "restAPI";
import TagsAPI from "restAPI/TagsAPI";
import { FilterInfo } from "types/pagination";
import { CustomColumnScope, CustomFindingColumn } from "types/tenantConfig";
import {
  CountType,
  OptionType,
  differentiateOptionsWithCount,
} from "utils/columnFilterUtil";
import { formatNumber } from "utils/numberUtil";
import Sorters from "utils/sorters";
import { CUSTOM_COLUMN_PREFIX } from "views/Findings/Constants";
import { extractTagValue } from "views/Findings/utils";

type CountAPIType = (
  name: string,
  searchText: string,
  filterInfo: FilterInfo,
) => CancellableAPIRequest<CountType[]>;

export default function ACCustomAttributeFilterDropdown({
  attribute,
  componentProps,
  scope,
  contextual,
  contextualFilterKeys,
  countAPI,
  useTagsAPIV2,
  ...restProps
}: {
  attribute: CustomFindingColumn;
  componentProps: object;
  scope?: CustomColumnScope;
  contextual?: boolean;
  contextualFilterKeys?: string[];
  countAPI?: CountAPIType;
  useTagsAPIV2?: boolean;
}) {
  const [options, setOptions] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState(true);
  const cancelRef = useRef(noop);
  const filters = useUrlFilters();

  // @ts-expect-error TODO fix type
  const filtersFromProps = restProps.filterInfo;

  // filters - filters from URL
  // filtersFromProps - filters from the filter bar component, both are different in case of widgets and both are same for dashboard/findings/etc
  // filters from dashboard/url should take priority
  const finalFilters = useMemo(() => {
    let _filters = omitBy(
      {
        ...filtersFromProps,
        ...filters,
      },
      isEmpty,
    );

    if (contextualFilterKeys?.length) {
      _filters = pick(_filters, contextualFilterKeys);
    }
    return omit(_filters, `${CUSTOM_COLUMN_PREFIX}${attribute.key}`);
  }, [filtersFromProps, filters, contextualFilterKeys, attribute.key]);

  const actualAppliedFilters = useDetectFilterChange(finalFilters);

  useEffect(() => {
    if (contextual) {
      setOptions([]);
    }
  }, [actualAppliedFilters, contextual]);

  const fetchValues = useMemo(() => {
    return (searchText: string) => {
      if (cancelRef.current) {
        cancelRef.current();
      }
      const finalScope =
        scope ||
        (includes(attribute.scope, CustomColumnScope.SUB_PRODUCT)
          ? CustomColumnScope.SUB_PRODUCT
          : includes(attribute.scope, CustomColumnScope.PRODUCT)
            ? CustomColumnScope.PRODUCT
            : attribute.scope[0]);
      let payload = {};
      const _filters = actualAppliedFilters;
      if (finalScope === CustomColumnScope.FINDING) {
        payload = {
          findingFilters: { filters: _filters },
        };
      } else if (finalScope === CustomColumnScope.PRODUCT) {
        payload = {
          productFilters: updateProductScopeFilters(_filters),
        };
      } else if (finalScope === CustomColumnScope.SUB_PRODUCT) {
        payload = {
          subProductFilters: updateSubProductScopeFilters(_filters),
        };
      }
      setLoading(true);
      let cancel = noop;
      let timeout: NodeJS.Timeout;
      if (useTagsAPIV2) {
        timeout = setTimeout(() => {
          const request = TagsAPI.fetchTagValuesV2({
            key: attribute.key,
            scope: finalScope,
            value: searchText,
            ...payload,
          });
          cancel = request.cancel;
          request.promise
            .then((values) => {
              const options: OptionType[] = map(
                values.data.tagsCountPair,
                (count, tag) => ({
                  id: tag,
                  name: tag,
                  secondaryText: formatNumber(count ?? 0),
                }),
              );
              setOptions((prev) =>
                uniqBy(
                  [...options.sort(Sorters.STRING("name")), ...prev],
                  "id",
                ),
              );
            })
            .finally(() => setLoading(false));
        }, 500);
      } else {
        timeout = setTimeout(() => {
          const showCount = isFunction(countAPI);
          const request = TagsAPI.fetchTagValues({
            key: attribute.key,
            scope: finalScope,
            value: searchText,
            // don't send filters if showCount, coz filters are sent in count API and list needs to show all with 0s and non 0s count
            ...(contextual && !showCount ? payload : undefined),
          });
          const countRequest = countAPI
            ? countAPI(attribute.key, searchText, _filters)
            : getEmptyCancellableRequest<CountType[]>([]);
          cancel = () => {
            request.cancel();
            countRequest.cancel();
          };
          Promise.all([request.promise, countRequest.promise])
            .then(([values, counts]) => {
              counts = counts.map(
                (count) =>
                  ({
                    ...count,
                    id: extractTagValue(count.id),
                  }) as CountType,
              );
              const options: OptionType[] = map(values.data.list, (item) => ({
                id: item.value,
                name: item.value,
              }));
              const { optionsWithCount, optionsWithoutCount } = showCount
                ? differentiateOptionsWithCount(options, counts)
                : { optionsWithCount: options, optionsWithoutCount: [] };
              setOptions((prev) =>
                uniqBy(
                  [
                    ...prev,
                    ...optionsWithCount.sort(Sorters.STRING("name")),
                    ...optionsWithoutCount.sort(Sorters.STRING("name")),
                  ],
                  "id",
                ),
              );
            })
            .finally(() => setLoading(false));
        }, 500);
      }

      cancelRef.current = () => {
        cancel();
        clearTimeout(timeout);
      };
    };
  }, [
    scope,
    attribute.scope,
    attribute.key,
    actualAppliedFilters,
    useTagsAPIV2,
    countAPI,
    contextual,
  ]);

  return (
    <ColumnFilter
      {...componentProps}
      filters={options}
      onSearchChange={fetchValues}
      loading={loading}
      refreshOnOpen={contextual}
      sortOptions={false}
      helpText="Showing top matching values. Search to narrow down or see more results."
    />
  );
}

const updateProductScopeFilters = (filters: FilterInfo) => {
  filters = { ...filters };
  if (filters.product) {
    filters.ids = filters.product;
    delete filters.product;
  }
  if (filters.productTags) {
    filters.keyValueTags = filters.productTags;
    delete filters.productTags;
  }
  if (filters.productStatus) {
    filters.status = filters.productStatus;
    delete filters.productStatus;
  }
  return filters;
};
const updateSubProductScopeFilters = (filters: FilterInfo) => {
  filters = { ...filters };
  if (filters.product) {
    filters.productIds = filters.product;
    delete filters.product;
  }
  if (filters.subProduct) {
    filters.subProductIds = filters.subProduct;
    delete filters.subProduct;
  }
  if (filters.subProductTags) {
    filters.tags = filters.subProductTags;
    delete filters.subProductTags;
  }
  if (filters.subProductStatuses) {
    filters.status = filters.subProductStatuses;
    delete filters.subProductStatuses;
  }
  return filters;
};
